import { Fragment, useState } from 'react'
import Konva from 'konva'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import { ShapeInteraction } from './shapeInteraction'
import { isClosedShape } from './entityPositions'
import { isIconShape } from './iconShapes'
import { ResolvedEntityBadge } from './entityAssignmentMatching'
import ShapeEntityMarkers from './ShapeEntityMarkers'
import RectShape from './RectShape'
import EllipseShape from './EllipseShape'
import TriangleShape from './TriangleShape'
import ArrowShape from './ArrowShape'
import LineShape from './LineShape'
import PencilShape from './PencilShape'
import TextShape from './TextShape'
import IconShapeView from './IconShape'

type Props = {
  shape: VisualShape
  width: number
  height: number
  interaction?: ShapeInteraction
  showEntityMarkers?: boolean
  zoomScale?: number
  entityStartNumber?: number
  entityAssignments?: Map<number, ResolvedEntityBadge>
  isEditingText?: boolean
  encounterId?: number
}

const ShapeRenderer = ({
  shape,
  width,
  height,
  interaction,
  showEntityMarkers = true,
  zoomScale = 1,
  entityStartNumber = 1,
  entityAssignments,
  isEditingText = false,
  encounterId,
}: Props) => {
  const [bodyNode, setBodyNode] = useState<Konva.Node | null>(null)
  const needsBodyNode =
    showEntityMarkers && isClosedShape(shape) && (shape.entity_count ?? 0) > 0

  const wrappedInteraction: ShapeInteraction | undefined = interaction
    ? {
        ...interaction,
        nodeRef: (node) => {
          interaction.nodeRef(node)
          if (needsBodyNode) setBodyNode(node)
        },
      }
    : undefined

  return (
    <Fragment key={shape.id}>
      {shape.type === 'rect' && (
        <RectShape
          shape={shape}
          width={width}
          height={height}
          interaction={wrappedInteraction}
        />
      )}
      {shape.type === 'circle' && (
        <EllipseShape
          shape={shape}
          width={width}
          height={height}
          interaction={wrappedInteraction}
        />
      )}
      {shape.type === 'triangle' && (
        <TriangleShape
          shape={shape}
          width={width}
          height={height}
          interaction={wrappedInteraction}
        />
      )}
      {shape.type === 'arrow' && (
        <ArrowShape
          shape={shape}
          width={width}
          height={height}
          interaction={wrappedInteraction}
        />
      )}
      {shape.type === 'line' && (
        <LineShape
          shape={shape}
          width={width}
          height={height}
          interaction={wrappedInteraction}
        />
      )}
      {shape.type === 'pencil' && (
        <PencilShape
          shape={shape}
          width={width}
          height={height}
          interaction={wrappedInteraction}
        />
      )}
      {shape.type === 'text' && (
        <TextShape
          shape={shape}
          width={width}
          height={height}
          interaction={wrappedInteraction}
          isEditing={isEditingText}
        />
      )}
      {isIconShape(shape) && (
        <IconShapeView
          shape={shape}
          width={width}
          height={height}
          interaction={interaction}
          showEntityBadge={showEntityMarkers && (shape.entity_count ?? 0) > 0}
          zoomScale={zoomScale}
          entityStartNumber={entityStartNumber}
          entityAssignments={entityAssignments}
          encounterId={encounterId}
        />
      )}
      {needsBodyNode && isClosedShape(shape) ? (
        <ShapeEntityMarkers
          shape={shape}
          width={width}
          height={height}
          bodyNode={bodyNode}
          zoomScale={zoomScale}
          startNumber={entityStartNumber}
          entityAssignments={entityAssignments}
        />
      ) : null}
    </Fragment>
  )
}

export default ShapeRenderer
